// utils/imageCompression.ts
// Image compression and optimization utility

import imageCompression from 'browser-image-compression';

/**
 * Configuration for image compression
 */
export const IMAGE_COMPRESSION_CONFIG = {
    // Maximum file size in MB
    maxSizeMB: 2,

    // Maximum width/height in pixels
    maxWidthOrHeight: 1920,

    // Use web worker for better performance
    useWebWorker: true,

    // File type for output
    fileType: 'image/jpeg',

    // Initial quality (0-1, where 1 is best quality)
    initialQuality: 0.8,
};

/**
 * Inline image specific configuration (smaller for embedded images)
 */
export const INLINE_IMAGE_CONFIG = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1200,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.75,
};

/**
 * Thumbnail specific configuration (very small)
 */
export const THUMBNAIL_CONFIG = {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 600,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.7,
};

/**
 * Compresses an image file
 * @param file - Original image file
 * @param config - Compression configuration (optional)
 * @returns Compressed image file
 */
export const compressImage = async (
    file: File,
    config = IMAGE_COMPRESSION_CONFIG
): Promise<File> => {
    try {
        console.log(`📸 Original image: ${file.name}, Size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);

        const compressedFile = await imageCompression(file, config);

        console.log(`✅ Compressed image: ${compressedFile.name}, Size: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);

        return compressedFile;
    } catch (error) {
        console.error('❌ Image compression failed:', error);
        throw new Error('Failed to compress image. Please try a different image.');
    }
};

/**
 * Compresses an inline image for article content
 * Uses smaller size limits for embedded images
 * @param file - Original image file
 * @returns Compressed image file
 */
export const compressInlineImage = async (file: File): Promise<File> => {
    return compressImage(file, INLINE_IMAGE_CONFIG);
};

/**
 * Compresses a thumbnail image
 * Uses very small size limits for preview images
 * @param file - Original image file
 * @returns Compressed thumbnail file
 */
export const compressThumbnail = async (file: File): Promise<File> => {
    return compressImage(file, THUMBNAIL_CONFIG);
};

/**
 * Validates image file before upload
 * @param file - Image file to validate
 * @returns Validation result with message
 */
export const validateImageFile = (file: File): {
    isValid: boolean;
    message: string;
} => {
    // Check if file exists
    if (!file) {
        return {
            isValid: false,
            message: 'No file selected',
        };
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
        return {
            isValid: false,
            message: 'Invalid file type. Please upload a JPG, PNG, GIF, or WebP image.',
        };
    }

    // Check file size (max 10MB before compression)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
        return {
            isValid: false,
            message: 'Image is too large. Please select an image smaller than 10MB.',
        };
    }

    // Check minimum dimensions
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            if (img.width < 100 || img.height < 100) {
                resolve({
                    isValid: false,
                    message: 'Image is too small. Minimum size is 100x100 pixels.',
                });
            } else {
                resolve({
                    isValid: true,
                    message: 'Image is valid',
                });
            }
        };
        img.onerror = () => {
            resolve({
                isValid: false,
                message: 'Failed to load image. Please try a different file.',
            });
        };
        img.src = URL.createObjectURL(file);
    }) as any; // Note: This should be properly typed in real implementation
};

/**
 * Gets image dimensions without loading the full image
 * @param file - Image file
 * @returns Image dimensions
 */
export const getImageDimensions = (file: File): Promise<{
    width: number;
    height: number;
}> => {
    return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
            resolve({
                width: img.width,
                height: img.height,
            });
            URL.revokeObjectURL(img.src);
        };

        img.onerror = () => {
            reject(new Error('Failed to load image'));
            URL.revokeObjectURL(img.src);
        };

        img.src = URL.createObjectURL(file);
    });
};

/**
 * Converts image file to base64 data URL
 * Useful for preview before upload
 * @param file - Image file
 * @returns Base64 data URL
 */
export const fileToDataURL = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            if (typeof reader.result === 'string') {
                resolve(reader.result);
            } else {
                reject(new Error('Failed to convert image to data URL'));
            }
        };

        reader.onerror = () => {
            reject(new Error('Failed to read image file'));
        };

        reader.readAsDataURL(file);
    });
};

/**
 * Creates a preview URL for an image file
 * Remember to revoke this URL when done using URL.revokeObjectURL()
 * @param file - Image file
 * @returns Object URL for preview
 */
export const createImagePreviewURL = (file: File): string => {
    return URL.createObjectURL(file);
};

/**
 * Estimates the compressed size of an image
 * @param file - Original image file
 * @param config - Compression configuration
 * @returns Estimated size in MB
 */
export const estimateCompressedSize = async (
    file: File,
    config = IMAGE_COMPRESSION_CONFIG
): Promise<number> => {
    try {
        const compressedFile = await compressImage(file, config);
        return compressedFile.size / 1024 / 1024; // Size in MB
    } catch (error) {
        console.error('Failed to estimate compressed size:', error);
        return 0;
    }
};

/**
 * Formats file size for display
 * @param bytes - File size in bytes
 * @returns Formatted string like "2.5 MB"
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Checks if image needs compression
 * @param file - Image file
 * @param maxSizeMB - Maximum size in MB
 * @returns True if image needs compression
 */
export const needsCompression = (file: File, maxSizeMB: number = 2): boolean => {
    const fileSizeMB = file.size / 1024 / 1024;
    return fileSizeMB > maxSizeMB;
};

const imageCompressionUtils = {
    compressImage,
    compressInlineImage,
    compressThumbnail,
    validateImageFile,
    getImageDimensions,
    fileToDataURL,
    createImagePreviewURL,
    estimateCompressedSize,
    formatFileSize,
    needsCompression,
};

export default imageCompressionUtils;